import { useLoaderData, useLocation, useSearchParams } from 'react-router';
import { api } from '@/lib/api';
import type { GoogleAuthConfig } from '@/lib/google-auth';
import { LoginForm } from './login-form';
import { GoogleSignIn } from './google-sign-in';

interface Methods {
  password: boolean;
  google: boolean;
  googleConfig: GoogleAuthConfig | null;
}

export async function loginLoader(): Promise<Methods> {
  return api<Methods>('/auth/methods', { auth: false }).catch(() => ({
    password: false,
    google: false,
    googleConfig: null,
  }));
}

export function LoginPage() {
  const loaderData = useLoaderData<typeof loginLoader>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const invitationToken =
    new URLSearchParams(location.hash.slice(1)).get('invitation') ?? undefined;
  const isInvitation = invitationToken !== undefined;
  const next = searchParams.get('next');
  const target = next && next.startsWith('/') && !next.startsWith('//') ? next : '/contacts';
  const methods = loaderData;
  const googleConfigured = methods.google && !!methods.googleConfig;

  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-xl border bg-white p-8 shadow-sm">
        <img src="/favicon.svg" alt="" className="mb-4 size-12" />
        <h1 className="mb-1 text-2xl">Facet Flow CRM</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {isInvitation
            ? 'Accept your invitation by signing in with the Google email address that received it. You’ll join as an Editor.'
            : methods.password
              ? 'Sign in with your @facetflow.io email and the team password.'
              : 'Sign in with your Google account.'}
        </p>
        {methods.password && !isInvitation && <LoginForm next={target} />}
        {googleConfigured && methods.googleConfig && (
          <div className={methods.password && !isInvitation ? 'mt-4 border-t pt-4' : ''}>
            <GoogleSignIn
              config={methods.googleConfig}
              next={target}
              invitationToken={invitationToken}
            />
          </div>
        )}
        {isInvitation && !googleConfigured && (
          <p role="alert" className="text-sm text-destructive">
            Google sign-in is unavailable. Please contact the admin who invited you.
          </p>
        )}
        {isInvitation && (
          <a href="/login" className="mt-4 block text-sm underline">
            Already joined? Sign in normally
          </a>
        )}
        {!isInvitation && !methods.password && !googleConfigured && (
          <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            No login method is configured. Set <code>CRM_PASSWORD</code> (or{' '}
            <code>GOOGLE_LOGIN_ENABLED=true</code>) in <code>backend/.env</code>.
          </p>
        )}
      </div>
    </main>
  );
}
